import * as Plotly from "plotly.js";
import React, { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Plot from "../../components/ThreediPlot";
import { Asset } from "../../types/assets";
import styles from "../Timeseries.module.css";

import {
  getBottom,
  scatterData,
  tabulateCircle,
  tabulateEgg,
  tabulateRectangle,
  tabulateTrapezium,
} from "./cross-section-graph-hook";

interface Props {
  asset: Asset;
}

export const CrossSectionGraph: React.FC<Props> = ({ asset }) => {
  const intl = useIntl();

  // https://app.zenhub.com/workspaces/modeller-interface-61e574104cc38a001fb287de/issues/gh/nens/threedi-schematisation-editor/202
  // https://docs.ranawaterintelligence.com/h_onedee_flow.html#cross-section-shapes
  const crossSection: Plotly.ScatterData | undefined = useMemo(() => {
    const crossSectionProperties = asset.properties as any;
    let done = false;
    let checkIndex = 1;

    while (!done) {
      if ("cross_section_table_" + checkIndex in crossSectionProperties) {
        done = true;
        const type = crossSectionProperties["cross_section_type_" + checkIndex];

        const bottom = getBottom(crossSectionProperties) || 0;
        // Closed rectangle = 0 - Open rectangle = 1
        if (type === 0 || type === 1) {
          const width = crossSectionProperties["cross_section_width"];
          const [x, y] = tabulateRectangle(width, type === 0);
          return scatterData(x, y, bottom);
        }

        // Circle = 2
        if (type === 2) {
          const width = crossSectionProperties["cross_section_width"];
          const [x, y] = tabulateCircle(width);
          return scatterData(x, y, bottom);
        }

        // Egg = 3 - Inverted egg = 8
        if (type === 3 || type === 8) {
          const width = crossSectionProperties["cross_section_width"];
          const [x, y] = tabulateTrapezium(tabulateEgg(width / 2, type === 8));
          return scatterData(x, y, bottom);
        }

        // Tabulated rectangle = 5 - Tabulated trapezium = 6
        if (type === 5 || type === 6) {
          const [x, y] = tabulateTrapezium(
            crossSectionProperties["cross_section_table_" + checkIndex],
            type === 5
          );
          return scatterData(x, y, bottom);
        }

        // YZ = 7
        if (type === 7) {
          const [y, z] = crossSectionProperties["cross_section_table_" + checkIndex];
          return {
            ...scatterData(z, y, bottom),
            hovertemplate: `%{x:.2f}, %{y:.2f}`,
          } as Plotly.ScatterData;
        }
      } else if (checkIndex > 3) done = true;
      checkIndex++;
    }
    return undefined;
  }, [asset]);

  if (!crossSection) return null;

  const minX = Math.min(...(crossSection.x as number[]));
  const maxX = Math.max(...(crossSection.x as number[]));
  const minY = Math.min(...(crossSection.y as number[]));
  const maxY = Math.max(...(crossSection.y as number[]));
  const paddingX = (maxX - minX) / 100;
  const paddingY = (maxY - minY) / 100;
  const rangeX = [minX - paddingX, maxX + paddingX];
  const rangeY = [minY - paddingY, maxY + paddingY];

  return (
    <div>
      <div className={styles.TimeseriesTitle}>
        <FormattedMessage id="crossSection.crossSectionTitle" defaultMessage="Cross-section" />
      </div>
      <Plot
        style={{
          position: "relative",
          display: "inline-block",
          border: "1px solid #707070",
          borderRadius: 5,
          overflow: "hidden",
        }}
        data={[crossSection]}
        layout={{
          // autosize: true, // autosize seems not working, not troubleshooted yet
          width: 335, // width and size hardcoded, but could be retrieved from javascript (might be dynamic css)
          height: 335,
          paper_bgcolor: "#f2f2ec",
          plot_bgcolor: "#f2f2ec",
          margin: {
            l: 40,
            r: 10,
            b: 40,
            t: 10,
          },
          legend: {
            // the x 1 is somehow needed to move the legend to the right. No idea why
            x: 1,
            xanchor: "right",
            // the y 100 is done to move the legend up to above the chart
            y: 100,
          },
          xaxis: {
            // visible: !!showXAxis,
            title: {
              text: intl.formatMessage({
                id: "profileLineInspecter.chartXAxisWidth",
                defaultMessage: "Width (m) &#8594;",
              }),
              standoff: 10,
            },
            range: rangeX,
          },
          yaxis: {
            title: {
              text: intl.formatMessage({
                id: "profileLineInspecter.chartYAxisHeight",
                defaultMessage: "Height (mMSL) &#8594;",
              }),
              standoff: 10,
            },
            range: rangeY,
          },
        }}
      />
    </div>
  );
};
